import React, { useEffect, useRef, useState } from "react"
import _ from "lodash"
// PR
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import {ContextMenu} from "primereact/contextmenu"
// Components
import CarouselModal, { CarouselData } from "components/table/carousel/carousel-modal"
import CarouselTrigger from "components/table/carousel/carousel-trigger"
import { field as _field } from "components/table/lib/column-field"
import MoveRowModal from "components/modal/move-row-modal"
import SearchHeader from "components/table/search-header"
import { TableAutomation, focusCellByDataId, focusFirstCell, focusFirstCellByRowIndex, pageToFirst, getPagingInfo, getFocusedRowIndex } from "components/table/table-automation"
import { TextEdit, NumberEdit } from "components/table/inputs"
import { NumberField } from "components/table/display"
import { DuplicateModal } from "components/modal/duplicate-confirmation"
// Controls
import Controls from "components/multifamily/rent-roll/controls/"
import NoteControls from "components/notes/"
// Data
import automationService from "data/automation"
import carouselService from "data/carousel"
import multifamilyService, { Multifamily } from "data/multifamily"
import { Inflation } from "data/inflation"
import keyService, * as chord from "data/key"
import propertyService from "data/property"
import { copy, paste, canPaste, getPasteInfo } from "data/lib/copy-paste"
import toggles from "feature-toggles"
import notificationService from "data/notification"
// Styles
import { RentRollTable_s } from "components/multifamily/rent-roll/rent-roll-styles"

export type ContextRow = Multifamily

type PropTypes = {
  tableId: string
  data: Multifamily[]
  defaults: object
  inflationData: Inflation[]
  selectedState: [ ContextRow[], ( selected: ContextRow[] ) => void ]
  flags: { deleteFlag: number, pasteFlag: number }
}

const MFRentRollTable = (props: PropTypes) => {
  const [ selected, setSelected ] = props.selectedState

  const usePrevious = (value) =>{
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  }

  const nonSystemMenu = [
    {label: 'Copy Cell', icon: 'pi pi-fw pi-copy', command: e => { if(contextRow.systeminput === "systemImmutable") return;
      copy(contextCell, contextRow)
    }},
    {label: 'Move Row', icon: 'pi pi-fw pi-sort-alt', command: e => {
      automationService.broadcast("MODAL_OPENING", {})
      setShowMove(true)
    }},
    {label: 'Duplicate Row', icon: 'pi pi-fw pi-clone', command: async e => { if(contextRow.systeminput) return;
      const mf = await multifamilyService.copy(contextRow.propertyid, contextRow.multifamilyid)
      if (!_.isEmpty(selected)) setSelected(selected.concat(mf as ContextRow))
    }},
    {label: 'Duplicate Row n Times', icon: 'pi pi-fw pi-clone', command: async e => { if(contextRow.systeminput) return;
      showDuplicateConfirmation(async (n) => {
        const multifamilys = await multifamilyService.copies(contextRow.propertyid, contextRow.multifamilyid, n)
        !_.isEmpty(selected) && setSelected(selected.concat(multifamilys as ContextRow[]))
      })
    }},
    {label: selected.length ? 'Delete Rows' : 'Delete Row', icon: 'pi pi-fw pi-trash', command: e => { if ( contextRow.systeminput ) return;
      const currentRowNum = getFocusedRowIndex() + 1
      showDeleteConfirmation(contextRow, currentRowNum)
    }}
  ]

  const [carouselData, updateCarouselData] = useState<CarouselData|null>(null);
  const [searchFilter, setSearchFilter] = useState<string>("")
  const [contextRow, setContextRow] = useState<ContextRow>({multifamilyid: 0, propertyid: 0, sortorder: 0, unitdescription: ''})
  const [contextCell, setContextCell] = useState<object|null>(null)
  const [showMove, setShowMove] = useState(false)
  const [deleteConfirmation, setDeleteConfirmation] = useState<JSX.Element | null>(null)
  const [duplicateConfirmation, setDuplicateConfirmation] = useState<JSX.Element | null>(null)
  const menuRef = useRef<ContextMenu | null>(null)
  const [menu, setMenu] = useState<[]|object[]>(nonSystemMenu)
  const prevSearch = usePrevious(searchFilter);
  const tableRef = useRef() as React.MutableRefObject<DataTable>
  const field = _field('multifamily')

  useEffect(() => {
    const carouselSubscription = carouselService.stream().subscribe(data => {
      updateCarouselData(data);
    })

    const multifamilyEventSubscription = multifamilyService.eventStream().subscribe(e => {
      switch(true) {
        case e.type === "MULTIFAMILY_CREATED":
          setTimeout(() => pageToFirst(props.tableId), 1)
          setTimeout(() => focusFirstCell(props.tableId), 500)
          break
      }
    })

    const keySubscription = keyService.stream().subscribe(k => {
      switch(true) {
        case k.chord === chord.CTRL_SHIFT_PLUS && k.down === true:
          if(!window[`${props.tableId}_carousel_in_focus`]) multifamilyService.create(propertyService.getStoredPropertyId())
          break
        case k.chord === chord.CTRL_SHIFT_MINUS && k.down === true:
          if(!window[`${props.tableId}_carousel_in_focus`]) {
            const currentRowNum = getFocusedRowIndex() + 1
            let pager = getPagingInfo(props.tableId)
            const record = _.find(multifamilyService.value(), mf => mf.sortorder === (currentRowNum + ((pager.currentPage - 1) * pager.pageSize)))
            if(record) {
              showDeleteConfirmation(record, currentRowNum)
            }
          }
          break
      }
    })

    return function cleanup() {
      carouselSubscription.unsubscribe()
      multifamilyEventSubscription.unsubscribe()
      keySubscription.unsubscribe()
    }
  }, [])


  useEffect(() => {
    if (prevSearch !== "" && searchFilter === "") _.invoke(tableRef, 'current.filter', '', 'unitdescription', 'contains');
    else if (searchFilter !== "") _.invoke(tableRef, 'current.filter', searchFilter, 'unitdescription', 'contains');
    else return
  }, [searchFilter])

  const persist = (value, field, rowData) => {
    const mfRentRoll = _.omit(_.cloneDeep(rowData), 'index') // strip fields added for table view
    _.set(mfRentRoll, field, value)

    if (field === 'unitinventorydetail.unitsOccupied'){
      _.set(mfRentRoll, 'unitinventorydetail.unitsVacant', _.get(rowData, ['unitinventorydetail', 'unitsTotal']) - value)
    }
    if (field === 'unitinventorydetail.unitsTotal'){
      _.set(mfRentRoll, 'unitinventorydetail.unitsVacant', value - _.get(rowData, ['unitinventorydetail','unitsOccupied']))
    }
    multifamilyService.patch(mfRentRoll)
    // If carousel, update carousel service so it will shoot back data to this component, update it's local state, and the modal will re-render
    if (value && (value.state || value.notes !== undefined)) carouselService.updateRowData(mfRentRoll);
  }

  const inflationData:object[] = props.inflationData;
  const defaults:object = props.defaults;

  useEffect(() => {
    setMenu(nonSystemMenu)
  }, [contextRow])

  const refresh = async () => {
    const { propertyid } = contextRow
    const mfs = await multifamilyService.fetchByPropertyId( propertyid )
    setSelected( _.intersectionBy( mfs, selected, 'multifamilyid' ) )
  }

  useEffect(() => {
    if ( canPaste( contextCell, selected ) ) {
      nonSystemMenu.splice( 1, 0, {
        label: selected.length ? 'Paste to Rows' : 'Paste Cell', icon: 'pi pi-fw pi-file', command: e => {
          if (selected.length > 1 ) {
            showPasteConfirmation( contextRow, -1 )
            return
          }
        paste(contextCell, contextRow, multifamilyService.fetchByPropertyId, 'multifamily', selected)
      }})
    }
    contextRow.systeminput === "systemImmutable" ? setMenu([]) : setMenu(nonSystemMenu)
  }, [contextCell, selected.length])

  useEffect( () => {
    if ( !props.flags.deleteFlag ) return //skip first time
    showDeleteConfirmation(_.first(selected), -1 )
  }, [ props.flags.deleteFlag ] )

  const showDeleteConfirmation = (record: ContextRow, currentRow: number) => {
    automationService.broadcast("MODAL_OPENING", {})
    setDeleteConfirmation(Controls.DeleteConfirmation({
      show: true,
      message: `Would you like to delete the ${selected.length} selected rows? Note this cannot be undone.`,
      close: () => {
        setDeleteConfirmation(null)
        setTimeout(() => automationService.broadcast("MODAL_CLOSING", {}), 200)
      },
      confirm: () => {
        if (selected.length > 1 ) {
          const propertyid = _.first(selected).propertyid
          multifamilyService.deleteMultiple(propertyid, _.map(selected, 'multifamilyid'))
        } else {
          multifamilyService.deleteOne(record.propertyid, record.multifamilyid)
        }
        setDeleteConfirmation(null)
        automationService.broadcast("MODAL_CLOSING", {})
        setTimeout(() => focusFirstCellByRowIndex(props.tableId, currentRow - 2 > 0 ? currentRow - 2 : 0), 200)
      },
      rowNum: record.sortorder,
      unitDescription: record.unitdescription,
      selected
    }))
  }

  const showDuplicateConfirmation = (copy: (n: number) => Promise<void>) => {
    const close = () => {
      setDuplicateConfirmation(null)
      focusCellByDataId( props.tableId, contextRow.multifamilyid)
    }
    setDuplicateConfirmation(
      <DuplicateModal
        close={close}
        copy={copy}
        show={true}
        title={contextRow.unitdescription}
      />
    )
  }

  useEffect( () => {
    if ( !props.flags.pasteFlag ) return //skip first time
    if ( !canPaste( contextCell, selected ) ) return
    showPasteConfirmation( contextRow, -1 )
  }, [ props.flags.pasteFlag ] )

  const showPasteConfirmation = ( record: ContextRow, currentRow: number ) => {
    automationService.broadcast( "MODAL_OPENING", {} )
    setDeleteConfirmation( Controls.DeleteConfirmation( {
      show: true,
      title: 'Paste',
      message: `Would you like to paste ${getPasteInfo()._meta.field} data to the ${selected.length} selected rows? Note this cannot be undone.`,
      close: () => {
        setDeleteConfirmation( null )
        setTimeout( () => automationService.broadcast( "MODAL_CLOSING", {} ), 200 )
      },
      confirm: () => {
        paste( contextCell, contextRow, refresh, 'multifamily', selected )
        setDeleteConfirmation(null)
        setTimeout(() => {
          focusFirstCellByRowIndex(props.tableId, currentRow - 2 > 0 ? currentRow - 2 : 0)
        }, 200)
      },
      rowNum: currentRow,
      unitDescription: record.unitdescription,
      selected
    }))
  }

  return (
    <div>
      <SearchHeader setSearchFilter={setSearchFilter} />
      <ContextMenu model={menu} ref={menuRef} appendTo={document.querySelector("body")} onHide={() => {}}/>
      <MoveRowModal show={showMove} rowNum={contextRow.sortorder} close={() => setShowMove(false)} move={(kind, value) => {
        if (!_.isFinite(value)) return setShowMove(false)
        const {propertyid, multifamilyid, sortorder} = contextRow
        const maxRows = _.size(props.data)
        const destination = {
          'down': Math.min(sortorder + 1, maxRows),
          'up': Math.max(sortorder - 1, 1),
          'to': Math.min(maxRows, Math.max(value, 1))
        }[kind]

        multifamilyService.move(propertyid, multifamilyid, destination).then(() => setShowMove(false))
      }}/>
      {defaults && inflationData.length > 0 && <RentRollTable_s id={`${props.tableId}-scrollable-container`}>
        <DataTable
          id={props.tableId}
          ref={tableRef}
          value={props.data}
          paginator={true}
          rows={20}
          rowsPerPageOptions={[10, 20, 50, 100, 500]}
          sortField="sortorder"
          sortOrder={1}
          scrollable={true}
          paginatorPosition="bottom"
          frozenWidth={ toggles.multiselect() ? "270px" : "225px" }
          onContextMenu={e => menuRef.current ? _.invoke(menuRef.current, "show", e.originalEvent) : null}
          contextMenuSelection={contextRow}
          onContextMenuSelectionChange={e => {
            setSelected([])
            setContextRow(e.value)
            setContextCell(e.originalEvent.target)
          }}
          stateStorage="custom"
          customSaveState={state => localStorage.setItem("primereact-table-preferences", JSON.stringify({rows: state.rows}))}
          customRestoreState={() => JSON.parse(localStorage.getItem("primereact-table-preferences")!)}
          selection={selected} onSelectionChange={(e) => {
            const selected = _.filter(e.value, (it) => it.systeminput !== "systemImmutable")
            setSelected(selected)
          }}
        >
          { toggles.multiselect() && <Column selectionMode="multiple" frozen={ true } style={ { minWidth: "45px", paddingLeft: "8px" } } /> }
          <Column header="#" frozen={true} style={{width: "45px"}}
            body={(rowData, props) => props.rowIndex + 1}
          />
          <Column style={{width: "180px"}} frozen={true} className="p-editable-column"
            header="Unit Description"
            {...field('unitdescription')}
            editor={props => TextEdit({persist, ...props})}
          />
          <Column style={{width: "150px"}} className="p-editable-column"
            header="Unit Start"
            {...field('unitstartdetail', (rowData, props) =>
              <CarouselTrigger field="unitstartdetail" rowData={rowData} title="Unit Start" persist={persist}
                controls={{single: {control: Controls.UnitStartEdit, names: {analysisStart: "Analysis Start", specifiedDate: "Specified Date"}}}}
                componentAtRest={Controls.UnitStartDisplay}
                width={471}
              />)}
          />
          <Column style={{width: "150px"}} className="p-editable-column"
            header="Unit End"
            {...field('unitenddetail', (rowData, props) =>
              <CarouselTrigger field="unitenddetail" rowData={rowData} title="Unit End" persist={persist}
                controls={{single: {control: Controls.UnitEndEdit, names: {analysisEnd: "Analysis End", specifiedDate: "Specified Date"}}}}
                componentAtRest={Controls.UnitEndDisplay}
                width={471}
              />)}
          />
          <Column style={{width: "60px"}}
            header="Area"
            {...field('unitinventorydetail.area',
            (rowData, props) => NumberField({readPrecision: 0, ...props}),
            (e) => _.isMatch(e, {path: ["unitinventorydetail"], type: "object.base"}))}
            editor={props => NumberEdit({persist, readPrecision: 2, writePrecision: 2, ...props})}
          />
          <Column style={{width: "60px"}}
            header="Total"
            {...field('unitinventorydetail.unitsTotal',
            (rowData, props) => NumberField({readPrecision: 0, ...props}),
            (e) => _.isMatch(e, {path: ["unitinventorydetail"], type: "object.base"}))}
            editor={props => NumberEdit({persist, ...props})}
          />
          <Column style={{width: "70px"}}
            header="Occupied"
            {...field('unitinventorydetail.unitsOccupied',
            (rowData, props) => NumberField({readPrecision: 0, ...props}),
            (e) => _.isMatch(e, {path: ["unitinventorydetail"], type: "object.base"}))}
            editor={props => NumberEdit({persist, ...props})}
          />
          <Column style={{width: "60px"}}
            header="Vacant"
            {...field('unitinventorydetail.unitsVacant', undefined, (e) => _.isMatch(e, {path: ["unitinventorydetail"], type: "object.base"}))}
          />
          <Column style={{width: "150px"}} className="p-editable-column"
            header="Occupied Rollover"
            {...field('occupiedrolloverdetail', (rowData, props) =>
              <CarouselTrigger field="occupiedrolloverdetail" rowData={rowData} title="Occupied Rollover" persist={persist}
                controls={{spreadEvenly: {control: Controls.OccupiedRolloverBasic, name: "Basic"}, detailed: {control: Controls.OccupiedRollverDetailed, name: "Detailed"}}}
                basicReadonly={[{literal: 'Spread'}, 'spreadMos', {literal: ' mos'}]}
                basicStates={['spreadEvenly']}
                hover={{
                  basic: [{key: ['occupiedrolloverdetail', 'spreadMos'], label: 'Spread Period (Mos)'}, {key: ['occupiedrolloverdetail', 'percentRollToMarket'], label: '% Roll to Market'}],
                  detailed: {single: [{key: ['occupiedrolloverdetail', 'percentRollToMarket'], label: '% Roll to Market'}], multiple: [{key: ['occupiedrolloverdetail', 'date'], label: 'Date'}, {key: ['occupiedrolloverdetail', 'unitsRolling'], label: 'Units Expiring'}]
                }}}
                width={492}
              />)}
          />
          <Column style={{width: "120px"}} className="p-editable-column"
            header="Vacant Lease-up"
            {...field('vacantleaseupdetail', (rowData, props) =>
              <CarouselTrigger field="vacantleaseupdetail" rowData={rowData} title="Vacant Lease-up" persist={persist}
              controls={{basic: {control: Controls.VacantLeaseupBasic, name: "Basic"}, detailed: {control: Controls.VacantLeaseupDetailed, name: "Detailed"}}}
              componentAtRest={Controls.VacantLeaseupDisplay}
              hover={{
                basic: [{key: ['vacantleaseupdetail', 'beginDate'], label: 'Begin Date'}, {key: ['vacantleaseupdetail', 'leaseupMos'], label: 'Leaseup Mos'}],
                detailed: {multiple: [{key: ['vacantleaseupdetail', 'date'], label: 'Date'}, {key: ['vacantleaseupdetail', 'unitsLeased'], label: 'Units Leased'}]
              }}}
              width={492}
            />)}
          />
          <Column style={{width: "95px"}}
            header="Market Term"
            {...field('marketterm', (rowData, props) => NumberField({readPrecision: 0, ...props}))}
            editor={props => NumberEdit({persist, ...props})}
          />
          <Column style={{width: "120px"}} className="p-editable-column"
            header="Rent"
            {...field('rentdetail', (rowData, props) =>
              <CarouselTrigger field="rentdetail" rowData={rowData} title="Rent" persist={persist} defaults={defaults}
                controls={{basic: {control: Controls.RentBasic, name: "Basic"}, detailed: {control: Controls.RentDetailed, name: "Detailed"}}}
                namedItems={{inflation: inflationData}}
                basicReadonly={['occupiedAmt', 'marketAmt']}
                hover={{
                  basic: [[{key: ['rentdetail', 'occupiedAmt']}, {key: ['rentdetail','occupiedAmtType']}, {key: ['rentdetail', 'occupiedInflationNamedItemId']}], [{key: ['rentdetail', 'marketAmt']}, {key: ['rentdetail', 'marketAmtType']}, {key: ['rentdetail', 'marketInflationNamedItemId']}]],
                  detailed: {multiple: [{key: ['rentdetail', 'date'], label: 'Date'}, {key: ['rentdetail', 'occupiedAmt'], label: 'Occupied'}, {key: ['rentdetail', 'marketAmt'], label: 'Market'}, {key: ['rentdetail', 'amtType'], label: 'Amt Type'}]
                }}}
                width={908}
              />)}
          />
          <Column style={{width: "120px"}} className="p-editable-column"
            header="Free Rent"
            {...field('freerentdetail', (rowData, props) =>
              <CarouselTrigger field="freerentdetail" rowData={rowData} title="Free Rent" persist={persist} defaults={defaults}
              controls={{basic: {control: Controls.FreeRentBasic, name: "Basic"}, detailed: {control: Controls.FreeRentDetailed, name: "Detailed"}}}
              basicReadonly={['occupiedMos', 'marketMos']}
              hover={{
                basic: [{key: ['freerentdetail', 'occupiedMos'], label: 'Occupied'}, {key: ['freerentdetail', 'marketMos'], label: 'Market'}, {key: ['freerentdetail', 'timing'], label: 'Timing'}],
                detailed: {single: [{key: ['freerentdetail', 'timing'], label: 'Timing'}], multiple: [{key: ['freerentdetail', 'date'], label: 'Date '}, {key: ['freerentdetail', 'occupiedMos'], label: 'Occupied'}, {key: ['freerentdetail', 'marketMos'], label: 'Market'}]
              }}}
              width={740}
            />)}
          />
          <Column style={{width: "120px"}} className="p-editable-column"
            header="Make Ready"
            {...field('makereadydetail', (rowData, props) =>
              <CarouselTrigger field="makereadydetail" rowData={rowData} title="Make Ready" persist={persist} defaults={defaults}
                controls={{basic: {control: Controls.MakeReadyBasic, name: "Basic"}, detailed: {control: Controls.MakeReadyDetailed, name: "Detailed"}}}
                namedItems={{inflation: inflationData}}
                basicReadonly={['amt']}
                hover={{
                  basic: [{key: ['makereadydetail', 'amt'], label: 'Amt'}, {key: ['makereadydetail', 'amtType'], label: 'Amt Type'}, {key: ['makereadydetail', 'inflationNamedItemId'], label: 'Inflation'}],
                  detailed: {single: [{key: ['makereadydetail', 'amtType'], label: 'Amt Type'}, {key: ['makereadydetail', 'inflationNamedItemId'], label: 'Inflation'}], multiple: [{key: ['makereadydetail', 'date'], label: 'Date '}, {key: ['makereadydetail', 'amt'], label: 'Amt'}]
                }}}
                width={594}
                />)}
          />
          <Column style={{width: "120px"}} className="p-editable-column"
            header="Commission"
            {...field('commissiondetail', (rowData, props) =>
              <CarouselTrigger field="commissiondetail" rowData={rowData} title="Commission" persist={persist} defaults={defaults}
                controls={{basic: {control: Controls.CommissionBasic, name: "Basic"}, detailed: {control: Controls.CommissionDetailed, name: "Detailed"}}}
                namedItems={{inflation: inflationData}}
                basicReadonly={['amt']}
                hover={{
                  basic: [{key: ['commissiondetail', 'amt'], label: 'Amt'}, {key: ['commissiondetail', 'amtType'], label: 'Amt Type'}, {key: ['commissiondetail', 'inflationNamedItemId'], label: 'Inflation'}],
                  detailed: {single: [{key: ['commissiondetail', 'amtType'], label: 'Amt Type'}, {key: ['commissiondetail', 'inflationNamedItemId'], label: 'Inflation'}], multiple: [{key: ['commissiondetail', 'date'], label: 'Date '}, {key: ['commissiondetail', 'amt'], label: 'Amt'}]
                }}}
                width={594}
              />)}
          />
          <Column style={{width: "120px"}} className="p-editable-column"
            header="Credit Loss"
            {...field('creditlossdetail', (rowData, props) =>
              <CarouselTrigger field="creditlossdetail" rowData={rowData} title="Credit Loss" persist={persist}
                controls={{basic: {control: Controls.CreditLossBasic, name: 'Basic'}, detailed: {control: Controls.CreditLossDetailed, name: 'Detailed'}}}
                basicReadonly={['percent']}
                hover={{
                  basic: [{key: ['creditlossdetail', 'percent'], label: 'Percent'}],
                  detailed: {multiple: [{key: ['creditlossdetail', 'date'], label: 'Date '}, {key: ['creditlossdetail', 'percent'], label: 'Percent'}]
                }}}
                width={492}
              />)}
          />
          <Column style={{width: "120px"}} className="p-editable-column"
            header="Vacancy Loss"
            {...field('vacancylossdetail', (rowData, props) =>
              <CarouselTrigger field="vacancylossdetail" rowData={rowData} title="Vacancy Loss" persist={persist}
                controls={{basic: {control: Controls.VacancyLossBasic, name: 'Basic'}, detailed: {control: Controls.VacancyLossDetailed, name: 'Detailed'}}}
                basicReadonly={['percent']}
                hover={{
                  basic: [{key: ['vacancylossdetail', 'percent'], label: 'Percent'}, {key: ['vacancylossdetail', 'reducePercentByVacancy'], label: 'Reduce % by Vacancy'}],
                  detailed: {single: [{key: ['vacancylossdetail', 'reducePercentByVacancy'], label: 'Reduce % by Vacancy'}], multiple: [{key: ['vacancylossdetail', 'date'], label: 'Date '}, {key: ['vacancylossdetail', 'percent'], label: 'Percent'}]
                }}}
                width={492}
              />)}
          />
          <Column style={{width: "100px"}} className="p-editable-column"
            header="Notes"
            {...field('notedetail', (rowData, props) =>
              <CarouselTrigger field="notedetail" rowData={rowData} title="Rent" persist={persist}
                controls={{single: {control: NoteControls.NotesEdit, name: "Notes"}}}
                componentAtRest={NoteControls.NotesDisplay} />)}
          />
        </DataTable>
        { carouselData && carouselData['show'] && <CarouselModal code="unitdescription" data={carouselData} key="carousel-modal" width={carouselData.width}/> }
        { deleteConfirmation }
        { duplicateConfirmation }
        { props.data.length ? <TableAutomation tableId={props.tableId}/> : null }
        <span className="selection-count">{ selected.length ? `Selected Rows: ${selected.length}` : null }</span>
      </RentRollTable_s>}
    </div>
  )
}

export default MFRentRollTable
